import { createTheme } from "@mui/material/styles";

/**
 * MallInHand - Premium Shopping Experience Design System
 *
 * Inspired by world-class shopping apps: Farfetch, Net-a-Porter, Westfield
 * Features luxury brand aesthetics with deep navy, gold accents, and sophisticated gradients.
 */

// Premium gradient color schemes inspired by luxury retail brands
export const gradients = {
  // Primary luxury gradients - Deep Navy & Royal Blue (trust, premium)
  navyLux: "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
  royalBlue: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",

  // Gold & Champagne accents (elegance, luxury)
  gold: "linear-gradient(135deg, #d4af37 0%, #FFD700 50%, #FFA000 100%)",
  champagne: "linear-gradient(135deg, #f4e5c3 0%, #d4af37 100%)",

  // Emerald & Jade (premium, exclusive)
  emerald: "linear-gradient(135deg, #0f9d58 0%, #10b368 50%, #0cbb6b 100%)",
  jade: "linear-gradient(135deg, #00695c 0%, #00897b 100%)",

  // Rose Gold & Coral (modern luxury)
  roseGold: "linear-gradient(135deg, #e8b298 0%, #c9a589 50%, #b08968 100%)",
  coral: "linear-gradient(135deg, #ff6b9d 0%, #c94b7a 100%)",

  // Deep Purple (sophistication)
  deepPurple: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #8e24aa 100%)",

  // Original gradients for backward compatibility
  purple: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  pink: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  cyan: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  green: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",

  // Secondary gradients
  greenAlt: "linear-gradient(135deg, #0f9d58 0%, #10b368 100%)",
  redAlt: "linear-gradient(135deg, #d32f2f 0%, #e57373 100%)",
  purpleAlt: "linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)",

  // Premium background gradients
  background: "linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)",
  darkBg: "linear-gradient(135deg, #1a237e 0%, #0d1642 100%)",
};

// Skeleton loading overlay colors for gradient backgrounds
export const skeletonOverlay = {
  light: "rgba(255,255,255,0.2)", // For dark gradient backgrounds
  dark: "rgba(0,0,0,0.1)", // For light backgrounds
};

// Common transparent colors used across the app
export const transparentColors = {
  white10: "rgba(255,255,255,0.1)",
  white15: "rgba(255,255,255,0.15)",
  white20: "rgba(255,255,255,0.2)",
  white25: "rgba(255,255,255,0.25)",
  white30: "rgba(255,255,255,0.3)",
  white60: "rgba(255,255,255,0.6)",
  white70: "rgba(255,255,255,0.7)",
  white80: "rgba(255,255,255,0.8)",
  white90: "rgba(255,255,255,0.9)",
  black05: "rgba(0,0,0,0.05)",
  black08: "rgba(0,0,0,0.08)",
  black10: "rgba(0,0,0,0.1)",
  black12: "rgba(0,0,0,0.12)",
  black15: "rgba(0,0,0,0.15)",
  black20: "rgba(0,0,0,0.2)",
  black30: "rgba(0,0,0,0.3)",
};

// Common box shadows - Premium depth and elevation
export const boxShadows = {
  subtle: "0 1px 3px rgba(26, 35, 126, 0.04)",
  light: "0 2px 8px rgba(26, 35, 126, 0.08)",
  medium: "0 4px 16px rgba(26, 35, 126, 0.12)",
  elevated: "0 8px 24px rgba(26, 35, 126, 0.14)",
  strong: "0 12px 32px rgba(26, 35, 126, 0.16)",
  deep: "0 20px 60px rgba(26, 35, 126, 0.25)",
  toolbar:
    "0 4px 20px rgba(26, 35, 126, 0.12), 0 2px 8px rgba(26, 35, 126, 0.08)",
  card: "0 8px 32px rgba(26, 35, 126, 0.08)",
  cardHover: "0 12px 48px rgba(26, 35, 126, 0.12)",
  primary: "0 4px 16px rgba(26, 35, 126, 0.25)",
  primaryHover: "0 8px 28px rgba(26, 35, 126, 0.35)",
  gold: "0 4px 16px rgba(212, 175, 55, 0.3)",
  goldHover: "0 8px 28px rgba(212, 175, 55, 0.4)",
};

// Border radius values
export const borderRadius = {
  small: 8,
  medium: 12,
  large: 16,
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#1a237e", // Deep Navy - Premium, Trust
      light: "#3949ab",
      dark: "#0d1642",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#d4af37", // Gold - Luxury, Elegance
      light: "#FFD700",
      dark: "#b8941f",
      contrastText: "#1a237e",
    },
    success: {
      main: "#0f9d58", // Emerald - Premium Green
      light: "#10b368",
      dark: "#0a7840",
    },
    error: {
      main: "#d32f2f",
      light: "#e57373",
      dark: "#c62828",
    },
    warning: {
      main: "#FFA000", // Amber Gold
      light: "#FFB74D",
      dark: "#F57C00",
    },
    info: {
      main: "#1976d2", // Royal Blue
      light: "#64b5f6",
      dark: "#1565c0",
    },
    background: {
      default: "#fafbfc", // Clean White-Gray
      paper: "#ffffff",
    },
    text: {
      primary: "#1a1a1a", // Almost Black
      secondary: "rgba(26, 26, 26, 0.7)",
      disabled: "rgba(26, 26, 26, 0.4)",
    },
    divider: "rgba(26, 35, 126, 0.08)", // Subtle Navy Divider
  },
  typography: {
    fontFamily:
      '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "3rem",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.75,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.57,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      letterSpacing: "0.00938em",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      letterSpacing: "0.01071em",
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      fontSize: "0.9375rem",
      letterSpacing: "0.02857em",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.66,
    },
  },
  spacing: 8, // Base spacing unit (1 = 8px)
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 2px 4px rgba(0,0,0,0.05)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 4px 12px rgba(0,0,0,0.12)",
    "0 8px 16px rgba(0,0,0,0.14)",
    "0 12px 24px rgba(0,0,0,0.16)",
    "0 16px 32px rgba(0,0,0,0.18)",
    "0 20px 40px rgba(0,0,0,0.2)",
    "0 24px 48px rgba(0,0,0,0.22)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
    "0 2px 8px rgba(0,0,0,0.1)",
  ],
  components: {
    MuiCard: {
      defaultProps: {
        elevation: 2,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.14)",
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          "&:last-child": {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 24px",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        },
        elevation2: {
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          letterSpacing: "0.5px",
          color: "rgba(0, 0, 0, 0.7)",
        },
        root: {
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(26, 35, 126, 0.04)",
            borderLeft: "3px solid #1a237e",
            transition: "all 0.2s ease-in-out",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.08)",
          "&::after": {
            background:
              "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
          },
        },
      },
    },
  },
});

export default theme;
